import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Button,
} from "@mui/material";
import SearchBar from "../../components/SearchBar";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const Support = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const chats = [
    {
      id: "CHT-1001",
      user: { name: "John Doe", email: "john@example.com" },
      subject: "Order delay",
      lastMessage: "Thanks for the update",
      date: "03-Sep-2025 10:30 AM",
    },
    {
      id: "CHT-1002",
      user: { name: "Jane Smith", email: "jane@example.com" },
      subject: "Refund status",
      lastMessage: "Please advise",
      date: "02-Sep-2025 05:15 PM",
    },
  ];

  const [queries, setQueries] = useState([
    {
      id: "QR-2001",
      user: { name: "Alex Lee", email: "alex@example.com" },
      subject: "Account verification",
      category: "Account",
      date: "01-Sep-2025",
      status: "Open",
    },
    {
      id: "QR-2002",
      user: { name: "Maria Garcia", email: "maria@example.com" },
      subject: "Product info",
      category: "Product",
      date: "01-Sep-2025",
      status: "Resolved",
    },
  ] as Array<{id:string; user:{name:string;email:string}; subject:string; category:string; date:string; status:'Open'|'Under Review'|'Resolved'}>);

  const [complaints, setComplaints] = useState([
    {
      id: "CMP-3001",
      user: { name: "Mike Johnson", email: "mike@example.com" },
      against: "Store #245",
      reason: "Seller unresponsive",
      date: "31-Aug-2025",
      status: "Under Review",
    },
    {
      id: "CMP-3002",
      user: { name: "Sarah Wilson", email: "sarah@example.com" },
      against: "Product PROD003",
      reason: "Item not as described",
      date: "30-Aug-2025",
      status: "Resolved",
    },
  ] as Array<{id:string; user:{name:string;email:string}; against:string; reason:string; date:string; status:'Open'|'Under Review'|'Resolved'}>);

  const statusColor = (status: string) => {
    if (status === 'Resolved') return 'success';
    if (status === 'Under Review') return 'warning';
    return 'default';
  };

  const updateQueryStatus = (id: string, status: 'Open'|'Under Review'|'Resolved') => {
    setQueries(prev => prev.map(q => q.id === id ? { ...q, status } : q));
  };

  const updateComplaintStatus = (id: string, status: 'Open'|'Under Review'|'Resolved') => {
    setComplaints(prev => prev.map(c => c.id === id ? { ...c, status } : c));
  };

  return (
    <>
      <div className="main_title">
        <h1>Manage Customer Support</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Manage Customer Support
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <Box className="custom_tabs1" sx={{ width: "100%" }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} className="custom_tabs_links">
                <Tab label="Chats" {...a11yProps(0)} />
                <Tab label="Queries Received" {...a11yProps(1)} />
                <Tab label="Complaints/Disputes" {...a11yProps(2)} />
              </Tabs>
            </Box>
          </div>
        </div>

        <CustomTabPanel value={tab} index={0}>
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setSearchTerm}
                placeholder="Search chats..."
              />
            </div>
          </div>
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow hover>
                  <TableCell>Chat ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Last Message</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {chats.map((c) => (
                  <TableRow hover key={c.id}>
                    <TableCell>{c.id}</TableCell>
                    <TableCell>
                      <div className="user_block">
                        <figure>
                          <img src="/static/images/user_placeholder.png" alt="User" />
                        </figure>
                        <p>
                          <b>{c.user.name}</b>
                          <span>{c.user.email}</span>
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{c.subject}</TableCell>
                    <TableCell>{c.lastMessage}</TableCell>
                    <TableCell>{c.date}</TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <IconButton onClick={() => navigate(`/customer-supports/chat`)}>
                          <img src="/static/images/eye_icon.svg" alt="View" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTabPanel>

        <CustomTabPanel value={tab} index={1}>
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setSearchTerm}
                placeholder="Search queries..."
              />
            </div>
          </div>
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow hover>
                  <TableCell>Query ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {queries.map((q) => (
                  <TableRow hover key={q.id}>
                    <TableCell>{q.id}</TableCell>
                    <TableCell>
                      <div className="user_block">
                        <figure>
                          <img src="/static/images/user_placeholder.png" alt="User" />
                        </figure>
                        <p>
                          <b>{q.user.name}</b>
                          <span>{q.user.email}</span>
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{q.subject}</TableCell>
                    <TableCell>{q.category}</TableCell>
                    <TableCell>{q.date}</TableCell>
                    <TableCell>
                      <Chip size="small" label={q.status} color={statusColor(q.status) as any} />
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        {/* <Button className="btn btn_grey" size="small" onClick={() => updateQueryStatus(q.id, 'Under Review')}>Mark Under Review</Button>
                        <Button className="btn btn_success" size="small" onClick={() => updateQueryStatus(q.id, 'Resolved')}>Resolve</Button> */}
                        <IconButton onClick={() => navigate(`/customer-supports/view`)}>
                          <img src="/static/images/eye_icon.svg" alt="View" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTabPanel>

        <CustomTabPanel value={tab} index={2}>
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setSearchTerm}
                placeholder="Search complaints..."
              />
            </div>
          </div>
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow hover>
                  <TableCell>Complaint ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Against</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {complaints.map((c) => (
                  <TableRow hover key={c.id}>
                    <TableCell>{c.id}</TableCell>
                    <TableCell>
                      <div className="user_block">
                        <figure>
                          <img src="/static/images/user_placeholder.png" alt="User" />
                        </figure>
                        <p>
                          <b>{c.user.name}</b>
                          <span>{c.user.email}</span>
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{c.against}</TableCell>
                    <TableCell>{c.reason}</TableCell>
                    <TableCell>{c.date}</TableCell>
                    <TableCell>
                      <Chip size="small" label={c.status} color={statusColor(c.status) as any} />
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        {/* <Button className="btn btn_grey" size="small" onClick={() => updateComplaintStatus(c.id, 'Under Review')}>Mark Under Review</Button>
                        <Button className="btn btn_success" size="small" onClick={() => updateComplaintStatus(c.id, 'Resolved')}>Resolve</Button> */}
                        <IconButton onClick={() => navigate(`/customer-supports/view`)}>
                          <img src="/static/images/eye_icon.svg" alt="View" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTabPanel>
      </div>
    </>
  );
};

export default Support;